import { type ReactNode } from 'react';
import { IconArrowUp, IconArrowDown, IconArrowsSort } from '@tabler/icons-react';
import { GabPagination } from './GabPagination';
import { GabSpinner } from './GabSpinner';

interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: T) => ReactNode;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
}

interface GabDataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  sortField?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (field: string, dir: 'asc' | 'desc') => void;
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  rowKey?: (row: T) => string | number;
  selectedIds?: Set<number | string>;
  onSelect?: (id: number | string, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
  striped?: boolean;
  compact?: boolean;
  className?: string;
}

export function GabDataTable<T extends Record<string, unknown>>({
  columns, data, total, page = 1, pageSize = 50, onPageChange,
  sortField, sortDir, onSort, loading, emptyMessage = 'No records found',
  onRowClick, rowKey, selectedIds, onSelect, onSelectAll, striped, compact, className = '',
}: GabDataTableProps<T>) {
  const tableClass = ['table', striped ? 'table-striped' : '', compact ? 'table-compact' : ''].filter(Boolean).join(' ');
  const hasSelection = !!onSelect;
  const allSelected = data.length > 0 && selectedIds && data.every(row => {
    const id = rowKey ? rowKey(row) : (row as Record<string, unknown>).record_id as number;
    return selectedIds.has(id);
  });

  function handleSort(col: Column<T>) {
    if (!col.sortable || !onSort) return;
    const newDir = sortField === col.key && sortDir === 'asc' ? 'desc' : 'asc';
    onSort(col.key, newDir);
  }

  function sortIcon(col: Column<T>) {
    if (!col.sortable) return null;
    if (sortField !== col.key) return <IconArrowsSort size={12} style={{ opacity: 0.3 }} />;
    return sortDir === 'asc' ? <IconArrowUp size={12} /> : <IconArrowDown size={12} />;
  }

  return (
    <div className={`table-container ${className}`}>
      <table className={tableClass}>
        <thead>
          <tr>
            {hasSelection && (
              <th style={{ width: 40 }}>
                <input type="checkbox" checked={allSelected} onChange={e => onSelectAll?.(e.target.checked)} />
              </th>
            )}
            {columns.map(col => (
              <th
                key={col.key}
                style={{ width: col.width, textAlign: col.align, cursor: col.sortable ? 'pointer' : undefined }}
                onClick={() => handleSort(col)}
                {...(col.sortable ? {
                  role: 'button',
                  tabIndex: 0,
                  'aria-sort': sortField === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none',
                  onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(col); } },
                } : {})}
              >
                <span className="flex items-center gap-1">
                  {col.label}
                  {sortIcon(col)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length + (hasSelection ? 1 : 0)} style={{ textAlign: 'center', padding: 'var(--spacing-8)' }}>
                <GabSpinner size="sm" />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (hasSelection ? 1 : 0)} style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-neutral-500)' }}>
                {emptyMessage}
              </td>
            </tr>
          ) : data.map((row, i) => {
            const id = rowKey ? rowKey(row) : (row as Record<string, unknown>).record_id as number ?? i;
            const isSelected = selectedIds?.has(id);
            return (
              <tr
                key={id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
                {...(onRowClick ? {
                  role: 'button',
                  tabIndex: 0,
                  onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); } },
                } : {})}
              >
                {hasSelection && (
                  <td onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={isSelected || false} onChange={e => onSelect?.(id, e.target.checked)} />
                  </td>
                )}
                {columns.map(col => (
                  <td key={col.key} style={{ textAlign: col.align }}>
                    {col.render ? col.render(row) : String(row[col.key] ?? '')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {total != null && onPageChange && (
        <div style={{ padding: 'var(--spacing-3) var(--spacing-4)' }}>
          <GabPagination page={page} pageSize={pageSize} total={total} onChange={onPageChange} />
        </div>
      )}
    </div>
  );
}
